/**
 * A tiny pub/sub bridge so ANY API call that hits a plan limit shows the app's
 * one line about it, with zero per-call-site wiring. The typed client (`api`)
 * calls `emitUpgrade` when the server answers 402 with an `upgrade` payload; the
 * mounted `<LimitNotice/>` subscribes and shows it.
 */
import type { UpgradeInfo } from "../../shared/types";

/** Which limit stopped the action, and the server's line about it. */
export type LimitHit = UpgradeInfo & { message: string };

type Listener = (hit: LimitHit) => void;

let listener: Listener | null = null;

/** The notice registers here on mount; returns an unsubscribe for cleanup. */
export function subscribeUpgrade(fn: Listener): () => void {
  listener = fn;
  return () => {
    if (listener === fn) listener = null;
  };
}

/** Fired by the API client on a 402 upgrade response. */
export function emitUpgrade(hit: LimitHit): void {
  listener?.(hit);
}

/**
 * A limit's line inside the phone app: the fact only, never how to get more (flashsuite-phone
 * docs/APP-MODE.md section 2). The server's own line asks to choose a plan or to upgrade for the
 * first two; a failed payment and a full library are told as facts already.
 */
export function inAppLimitLine(reason: LimitHit["reason"], serverLine: string, business = "this business"): string {
  if (reason === "subscription") return `Flashroom is not on for ${business} yet.`;
  if (reason === "contacts") return "Your contacts are at this plan's limit.";
  return serverLine;
}
